/**
 * RESPONSIBILITY: give a @List row an identity that outlives its position.
 *
 * A row is addressed by this and never by index. `#event/2` points at whatever is second
 * right now, so it means something different the moment an editor drags a row — silently,
 * and only for people following old links. Filenames used to be keyed on row index for
 * the same reason and had the same bug.
 *
 * Generated once when the row is added, never edited, never shown, never reused. It is
 * kept OUT of the annotations on purpose: it is not a field an editor declares or fills,
 * so no schema describes it and no control renders it.
 */
import { ROW_ID } from './decorators/types';

export { ROW_ID };

/**
 * Short, URL-safe, and random rather than sequential — a counter would collide the
 * moment two editors added a row to the same list at once.
 *
 * `crypto.randomUUID` is unavailable on insecure origins, so this falls back to
 * getRandomValues, which is not. 12 base-36 characters is ~62 bits: enough that a
 * collision inside one list is not a thing that happens.
 */
export function newRowId(): string {
  const bytes = new Uint8Array(9);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 12);
}

/**
 * The row with this id, or undefined. What a deep link resolves through.
 *
 * Constrained to `object`, not `Record<string, unknown>`: a row is typed by the @Row
 * class an app declared, and a declared class has no index signature — requiring one
 * would push a cast to every call site, which is where casts go wrong.
 */
export const rowById = <T extends object>(
  rows: readonly T[] | undefined, id: string | undefined,
): T | undefined =>
  (!rows || !id
    ? undefined
    : rows.find((r) => (r as Record<string, unknown>)[ROW_ID] === id));

/** A row's id, for building a link to it. Empty when the row predates this. */
export const idOf = (row: object | undefined): string => {
  const v = row ? (row as Record<string, unknown>)[ROW_ID] : undefined;
  return typeof v === 'string' ? v : '';
};
